import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';

import { useSelector } from 'react-redux';

import customerApi from 'api/contacts';
import IUserGet from 'api/interfaces/users/IUserGet';
import { makeEmail } from 'services/contacts/emailFormats';
import {
  formatPhone,
  getCountryCode,
  withCountryCode,
} from 'services/contacts/phoneNumbers';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import TextInputField from 'components/layout/forms/TextInputField';
import translations from 'services/translations/translations';
import ContactTitleField from 'components/contacts/ContactTitleField';

export const NEW_COMPANY = 'company';
export const NEW_PERSON = 'person';

// The last choice of the person list of a row: opens the window of a new person of its
// company, so no link is needed between the rows
export const NEW_PERSON_CHOICE = '__new-person__';

// The types of the contacts (backend ContactTypes) the tasks of the code are among
const CONTACT_TYPES = [
  'constructor',
  'contractor',
  'designer',
  'developer',
  'inspector',
  'other',
];

const emptyCompany = {
  businessId: '',
  city: '',
  companyName: '',
  country: 'Suomi',
  postalNumber: '',
  streetAddress: '',
};

const emptyPerson = {
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  title: '',
};

/**
 * Creates a company or a contact person in the contacts without leaving the offer: the new
 * company or person is chosen on the row of the project contacts. A new company gets the
 * role of the row as its contact type and the department of the user, so it is found in
 * the searches of the user's offers.
 */
const NewProjectContactModal = ({
  company,
  mode,
  onCreated,
  organizationUnitId,
  role,
  toggle,
}: {
  // The company of the new contact person
  company: { label: string; value: string } | null;
  // NEW_COMPANY or NEW_PERSON, null when closed
  mode: null | string;
  onCreated: (created: { id: number; label: string }) => void;
  organizationUnitId: number | undefined;
  // The role of the row, the contact type of a new company
  role: string;
  toggle: () => void;
}): JSX.Element => {
  const { t } = useTranslation([
    translations.offers,
    translations.contacts,
    translations.common,
  ]);
  const [companyForm, setCompanyForm] = useState(emptyCompany);
  const [personForm, setPersonForm] = useState(emptyPerson);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<null | string>(null);

  // A new form every time the window is opened
  useEffect(() => {
    if (mode !== null) {
      setCompanyForm(emptyCompany);
      setPersonForm(emptyPerson);
      setError(null);
    }
  }, [mode]);

  const tr = (key: string) =>
    t(`${translations.offers}:projectContacts.${key}`);

  const changeCompany = (event: any) => {
    const { name, value } = event.target;
    setCompanyForm((current) => ({ ...current, [name]: value }));
  };

  // The company of a new person: its e-mail form and country, as on the contact pages the
  // e-mail is made of the names and a mobile number is spaced as XXX XXX XXXX
  const departmentCountry =
    useSelector(
      (state: { users: { current: IUserGet | null } }) => state.users.current
    )?.organizationUnit?.country ?? null;
  const [companyOfPerson, setCompanyOfPerson] = useState<{
    country: null | string;
    emailEnding: null | string;
    emailFormat: null | string;
  }>({ country: null, emailEnding: null, emailFormat: null });
  const [emailTyped, setEmailTyped] = useState(false);

  useEffect(() => {
    setEmailTyped(false);
    if (mode !== NEW_PERSON || company === null) {
      return undefined;
    }
    let active = true;
    customerApi
      .getContact(company.value)
      .then((result: any) => {
        if (active) {
          setCompanyOfPerson({
            country: result?.address?.country ?? null,
            emailEnding: result?.emailEnding ?? null,
            emailFormat: result?.emailFormat ?? null,
          });
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [company, mode]);

  const changePerson = (event: any) => {
    const { name } = event.target;
    const value =
      name === 'phone' ? formatPhone(event.target.value) : event.target.value;
    if (name === 'email') {
      setEmailTyped(value !== '');
    }
    setPersonForm((current) => {
      const changed = { ...current, [name]: value };
      return !emailTyped && (name === 'firstName' || name === 'lastName')
        ? {
            ...changed,
            email: makeEmail(
              companyOfPerson.emailFormat,
              changed.firstName,
              changed.lastName,
              companyOfPerson.emailEnding
            ),
          }
        : changed;
    });
  };

  const organizationUnitIds =
    organizationUnitId === undefined ? [] : [organizationUnitId];

  const save = async () => {
    setError(null);

    if (mode === NEW_COMPANY && companyForm.companyName.trim() === '') {
      setError(tr('Company name required'));
      return;
    }

    if (
      mode === NEW_PERSON &&
      personForm.firstName.trim() === '' &&
      personForm.lastName.trim() === ''
    ) {
      setError(tr('Person name required'));
      return;
    }

    setSaving(true);

    try {
      if (mode === NEW_COMPANY) {
        const result = await customerApi.post({
          address: {
            city: companyForm.city.trim(),
            country: companyForm.country.trim(),
            postalNumber: companyForm.postalNumber.trim(),
            streetAddress: companyForm.streetAddress.trim(),
          },
          businessId: companyForm.businessId.trim(),
          companyName: companyForm.companyName.trim(),
          // A task the admins have added is not a contact type: then "other"
          contactTypes: [CONTACT_TYPES.includes(role) ? role : 'other'],
          emailEnding: '',
          organizationUnitIds,
        });
        onCreated({ id: result.id, label: companyForm.companyName.trim() });
      } else if (mode === NEW_PERSON && company !== null) {
        const result = await customerApi.postPerson(company.value, {
          email: personForm.email.trim(),
          firstName: personForm.firstName.trim(),
          jobDescription: '',
          lastName: personForm.lastName.trim(),
          organizationUnitIds,
          phone: personForm.phone.trim(),
          phoneWithCountryCode: withCountryCode(
            personForm.phone,
            getCountryCode(companyOfPerson.country, departmentCountry)
          ),
          title: personForm.title.trim(),
        });
        onCreated({
          id: result.id,
          label:
            `${personForm.firstName.trim()} ${personForm.lastName.trim()}`.trim(),
        });
      }
    } catch (saveError) {
      setError(
        saveError?.response?.status === 403
          ? tr('No permission to add contacts')
          : tr('Save failed')
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      className="new-project-contact-modal"
      isOpen={mode !== null}
      toggle={toggle}
      size="lg"
    >
      <ModalHeader toggle={toggle}>
        {mode === NEW_COMPANY
          ? tr('New company')
          : `${tr('New person')}: ${company?.label ?? ''}`}
      </ModalHeader>
      <ModalBody>
        {mode === NEW_COMPANY ? (
          <>
            <Row>
              <Col md={8}>
                <TextInputField
                  id="new-project-contact-company-name"
                  label={t(`${translations.contacts}:Company Name`)}
                  name="companyName"
                  onChange={changeCompany}
                  required
                  value={companyForm.companyName}
                />
              </Col>
              <Col md={4}>
                <TextInputField
                  id="new-project-contact-business-id"
                  label={t(`${translations.contacts}:Business Id`)}
                  name="businessId"
                  onChange={changeCompany}
                  value={companyForm.businessId}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <TextInputField
                  id="new-project-contact-street"
                  label={t(`${translations.contacts}:Street Address`)}
                  name="streetAddress"
                  onChange={changeCompany}
                  value={companyForm.streetAddress}
                />
              </Col>
              <Col md={2}>
                <TextInputField
                  id="new-project-contact-postal"
                  label={t(`${translations.contacts}:Postal Number`)}
                  name="postalNumber"
                  onChange={changeCompany}
                  value={companyForm.postalNumber}
                />
              </Col>
              <Col md={2}>
                <TextInputField
                  id="new-project-contact-city"
                  label={t(`${translations.contacts}:City`)}
                  name="city"
                  onChange={changeCompany}
                  value={companyForm.city}
                />
              </Col>
              <Col md={2}>
                <TextInputField
                  id="new-project-contact-country"
                  label={t(`${translations.contacts}:Country`)}
                  name="country"
                  onChange={changeCompany}
                  value={companyForm.country}
                />
              </Col>
            </Row>
            <p className="project-contacts-info">{tr('New company info')}</p>
          </>
        ) : (
          <>
            <Row>
              <Col md={6}>
                <TextInputField
                  id="new-project-contact-first-name"
                  label={t(`${translations.contacts}:First Name`)}
                  name="firstName"
                  onChange={changePerson}
                  value={personForm.firstName}
                />
              </Col>
              <Col md={6}>
                <TextInputField
                  id="new-project-contact-last-name"
                  label={t(`${translations.contacts}:Last Name`)}
                  name="lastName"
                  onChange={changePerson}
                  value={personForm.lastName}
                />
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <ContactTitleField
                  label={t(`${translations.contacts}:Title`)}
                  onChange={changePerson}
                  value={personForm.title}
                />
              </Col>
              <Col md={4}>
                <TextInputField
                  id="new-project-contact-email"
                  label={tr('Email')}
                  name="email"
                  onChange={changePerson}
                  value={personForm.email}
                />
              </Col>
              <Col md={4}>
                <TextInputField
                  id="new-project-contact-phone"
                  label={t(`${translations.contacts}:Phone`)}
                  name="phone"
                  onChange={changePerson}
                  value={personForm.phone}
                />
              </Col>
            </Row>
          </>
        )}
        {error === null ? null : <p className="text-danger">{error}</p>}
      </ModalBody>
      <ModalFooter>
        <PrimaryButton className="outline-button" onClick={toggle}>
          {t(`${translations.common}:Cancel`).toLocaleUpperCase()}
        </PrimaryButton>
        <PrimaryButton disabled={saving} onClick={save}>
          {t(`${translations.common}:Save`).toLocaleUpperCase()}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};

export default NewProjectContactModal;
